<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('unavailable.title') }}</span>
        </div>
      </template>
      
      <div class="unavailable-form">
        <div class="form-row">
          <label class="form-label">{{ $t('unavailable.teacher') }}</label>
          <div class="form-content">
            <el-select 
              v-model="form.teacherId" 
              :placeholder="$t('unavailable.selectTeacher')"
              :filterable="false"
              style="width: 100%"
            >
              <el-option
                v-for="teacher in teachers"
                :key="teacher.id"
                :label="teacher.name"
                :value="teacher.id"
              />
            </el-select>
          </div>
        </div>
        
        <!-- Weekly Calendar Date Selection -->
        <div class="form-row full-row">
          <label class="form-label">{{ $t('unavailable.startDate') }}</label>
          <div class="form-content">
            <div class="week-navigation">
              <el-button 
                size="small" 
                :icon="ArrowLeft" 
                circle 
                :disabled="isCurrentWeek"
                @click="navigateWeek(-1)"
              />
              <span class="week-label">{{ currentWeekLabel }}</span>
              <el-button 
                size="small" 
                :icon="ArrowRight" 
                circle 
                @click="navigateWeek(1)"
              />
            </div>
            
            <div class="available-dates">
              <div
                v-for="date in weekDates"
                :key="date.value"
                :class="['date-item', { 
                  'is-selected': form.date === date.value,
                  'is-past': date.isPast,
                  'is-disabled': date.isPast
                }]"
                @click="date.isPast ? null : selectDate(date.value)"
              >
                <div class="date-value">{{ date.label }}</div>
                <div class="date-weekday">{{ date.weekday }}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="form-row full-row">
          <div class="form-label-with-count">
            <label class="form-label">{{ $t('unavailable.unavailableTimeSlots') }}</label>
            <span class="selected-count">{{ $t('unavailable.selected') }}: {{ selectedSlots.length }}</span>
          </div>
          <div class="form-content">
            <div class="slots-actions-top">
              <el-button size="small" @click="selectAllSlots">{{ $t('unavailable.selectAll') }}</el-button>
              <el-button size="small" @click="clearSelection">{{ $t('unavailable.clear') }}</el-button>
            </div>
            
            <div class="time-slots-grid">
              <div 
                v-for="slot in timeSlots" 
                :key="slot.value"
                class="time-slot"
                :class="{ selected: selectedSlots.includes(slot.value) }"
                @click="toggleSlot(slot.value)"
              >
                {{ slot.label }}
              </div>
            </div>
          </div>
        </div>
        
        <div class="form-row full-row">
          <div class="form-content">
            <el-button 
              type="primary" 
              class="save-btn"
              style="width: 100%"
              :loading="loading"
              @click="handleSubmit"
            >
              {{ $t('common.save') }}
            </el-button>
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- Unavailable Times List -->
    <el-card v-if="form.teacherId && form.date" style="margin-top: 20px;">
      <div class="list-header">
        <span class="list-title">{{ $t('unavailable.setUnavailableTimes') }}</span>
      </div>
      
      <div class="list-content" v-loading="listLoading">
        <div v-if="unavailableList.length === 0" class="empty-state">
          <el-empty :description="$t('unavailable.noUnavailableTimes')" :image-size="80" />
        </div>
        
        <div v-else class="unavailable-items">
          <div 
            v-for="(item, index) in unavailableList" 
            :key="index"
            class="unavailable-item"
          >
            <div class="item-info">
              <div class="item-teacher">{{ item.teacherName || item.uTeacherName || $t('unavailable.coach') }}</div>
              <div class="item-date">{{ formatDate(item.date || item.uDate || form.date) }}</div>
              <div class="item-hours">
                <el-tag 
                  v-for="(timeRange, idx) in formatTimeRanges(item.hours || item.uHours)" 
                  :key="idx"
                  type="warning"
                  size="small"
                  style="margin-right: 6px; margin-bottom: 4px"
                >
                  {{ timeRange }}
                </el-tag>
              </div>
            </div>
            <el-button 
              type="danger"
              size="small"
              plain
              @click="handleDelete(item)"
            >
              {{ $t('common.delete') }}
            </el-button>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import { getTeacherList } from '@/api/teacher'
import { setUnavailableTime, getUnavailableTime, getWeeklyCalendarTemplates } from '@/api/schedule'
import MTime from '@/utils/time-handler'
import dayjs from 'dayjs'

const { t } = useI18n()

const teachers = ref([])
const loading = ref(false)
const unavailableList = ref([])
const listLoading = ref(false)
const weeklyCalendarTemplates = ref([]) // Store weekly calendar templates
const timeSlots = ref([]) // Dynamic time slots based on weekly calendar

const form = reactive({
  teacherId: '',
  date: MTime.getCurrentDate()
})

// Weekly calendar state - starts from today
const currentWeekStart = ref(dayjs()) // Start from today
const weekDates = ref([]) // 7 days (today + next 6 days)

// Computed: Check if current view is the first week (starting today)
const isCurrentWeek = computed(() => {
  const today = dayjs().startOf('day')
  return currentWeekStart.value.isSame(today, 'day')
})

// Computed: Current week label
const currentWeekLabel = computed(() => {
  const start = currentWeekStart.value
  const end = start.add(6, 'day')
  const currentYear = dayjs().year()
  const startYear = start.year()
  const endYear = end.year()
  
  if (startYear === currentYear && endYear === currentYear) {
    // Same year
    return `${start.format('MM.DD')} - ${end.format('MM.DD')}`
  } else if (startYear !== endYear) {
    // Different years
    return `${start.format('YYYY.MM.DD')} - ${end.format('YYYY.MM.DD')}`
  } else {
    return `${start.format('MM.DD')} - ${end.format('MM.DD')}`
  }
})

// Watch for teacher or date changes to load existing unavailable times
watch([() => form.teacherId, () => form.date], async () => {
  // Always regenerate time slots when date changes (even without teacher selected)
  generateTimeSlots()
  
  if (form.teacherId && form.date) {
    // Clear previous selection when date changes
    selectedSlots.value = []
    await loadUnavailableList()
    // Auto-select existing unavailable time slots
    autoSelectExistingSlots()
  } else {
    selectedSlots.value = []
  }
})

// Auto-select existing unavailable time slots
const autoSelectExistingSlots = () => {
  const existingHours = new Set()
  unavailableList.value.forEach(item => {
    const hourStr = item.hours || item.uHours || ''
    if (hourStr === '[]' || !hourStr) return
    
    const match = hourStr.match(/\[(.+)\]/)
    if (!match) return
    
    const numbers = match[1].split(',').map(n => parseInt(n.trim()))
    
    numbers.forEach(h => {
      existingHours.add(`${String(h).padStart(2, '0')}:00`)
    })
  })
  
  selectedSlots.value = timeSlots.value
    .filter(slot => existingHours.has(slot.value))
    .map(slot => slot.value)
}

// Generate time slots based on weekly calendar template for the selected date
const generateTimeSlots = () => {
  if (!form.date) {
    // Default fallback if no date selected
    timeSlots.value = Array.from({ length: 16 }, (_, i) => {
      const hour = i + 8
      return { label: `${String(hour).padStart(2, '0')}:00`, value: `${String(hour).padStart(2, '0')}:00` }
    })
    return
  }
  
  const selectedDate = dayjs(form.date)
  const today = dayjs()
  const isToday = selectedDate.isSame(today, 'day')
  const currentHour = today.hour()
  
  const dayOfWeek = selectedDate.day() // 0=Sunday, 1=Monday, ..., 6=Saturday
  const dayTemplate = weeklyCalendarTemplates.value.find(t => t.dayOfWeek === dayOfWeek && t.isEnabled)
  
  if (dayTemplate && dayTemplate.availableHours && Array.isArray(dayTemplate.availableHours)) {
    // Use available hours from weekly calendar template
    let slots = dayTemplate.availableHours.map(hourNum => ({
      label: `${String(hourNum).padStart(2, '0')}:00`,
      value: `${String(hourNum).padStart(2, '0')}:00`,
      hour: hourNum
    }))
    
    // If today, filter out past hours (exclude current hour and past hours)
    if (isToday) {
      slots = slots.filter(slot => slot.hour > currentHour)
    }
    
    timeSlots.value = slots.map(({ hour, ...rest }) => rest)
  } else {
    // Fallback: use all hours from 08:00 to 23:00
    let slots = Array.from({ length: 16 }, (_, i) => {
      const hour = i + 8
      return { label: `${String(hour).padStart(2, '0')}:00`, value: `${String(hour).padStart(2, '0')}:00`, hour: hour }
    })
    
    // If today, filter out past hours (exclude current hour and past hours)
    if (isToday) {
      slots = slots.filter(slot => slot.hour > currentHour)
    }
    
    timeSlots.value = slots.map(({ hour, ...rest }) => rest)
  }
}

const formatTimeRanges = (hoursStr) => {
  if (!hoursStr || hoursStr === '[]') return []
  
  const match = hoursStr.match(/\[(.+)\]/)
  if (!match) return []
  
  const numbers = match[1].split(',').map(n => parseInt(n.trim()))
  
  return numbers
    .sort((a, b) => a - b)
    .map(h => `${String(h).padStart(2, '0')}:00`)
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const selectedSlots = ref([])

// Computed property to get unavailable hours as a Set for fast lookup
const unavailableHoursSet = computed(() => {
  const hours = new Set()
  unavailableList.value.forEach(item => {
    const hourStr = item.hours || item.uHours || ''
    if (hourStr === '[]' || !hourStr) return
    
    const match = hourStr.match(/\[(.+)\]/)
    if (!match) return
    
    const numbers = match[1].split(',').map(n => parseInt(n.trim()))
    
    numbers.forEach(h => {
      hours.add(`${String(h).padStart(2, '0')}:00`)
    })
  })
  return hours
})

const loadTeachers = async () => {
  try {
    const response = await getTeacherList()
    
    // Handle both array and object with list property
    let teacherList = []
    if (Array.isArray(response.data)) {
      teacherList = response.data
    } else if (response.data?.list) {
      teacherList = response.data.list
    } else if (response.data) {
      // Single object or other structure
      teacherList = [response.data]
    }
    
    // Filter only active teachers (status=1)
    teachers.value = teacherList.filter(t => t.status === 1)
    
    // If no teachers, show info message instead of error
    if (teachers.value.length === 0) {
      ElMessage.info(t('unavailable.noTeachersAvailable'))
    }
  } catch (error) {
    console.error('Failed to load teachers:', error)
    // Only show error if it's not the "no teachers found" message
    if (!error.message.includes('No teachers found')) {
      ElMessage.error(t('unavailable.failedToLoadTeachers'))
    }
    teachers.value = []
  }
}

const toggleSlot = (slotValue) => {
  const index = selectedSlots.value.indexOf(slotValue)
  if (index > -1) {
    selectedSlots.value.splice(index, 1)
  } else {
    selectedSlots.value.push(slotValue)
  }
}

const selectAllSlots = () => {
  selectedSlots.value = timeSlots.value.map(slot => slot.value)
}

const clearSelection = () => {
  selectedSlots.value = []
}

const handleSubmit = async () => {
  if (!form.teacherId) {
    ElMessage.warning(t('unavailable.selectTeacherWarning'))
    return
  }
  
  if (!form.date) {
    ElMessage.warning(t('unavailable.selectDateWarning'))
    return
  }
  
  if (selectedSlots.value.length === 0) {
    ElMessage.warning(t('unavailable.selectSlotWarning'))
    return
  }
  
  loading.value = true
  try {
    const hours = selectedSlots.value.map(slot => parseInt(slot.split(':')[0]))
    hours.sort((a, b) => a - b)
    
    const payload = {
      uTeacherId: form.teacherId,
      uDate: form.date,
      uHours: `[${hours.join(',')}]`
    }
    
    console.log('Sending payload:', payload)
    
    const response = await setUnavailableTime(payload)
    
    console.log('API response:', response)
    
    ElMessage.success(t('unavailable.saveSuccess'))
    await loadUnavailableList()
    // Wait for next tick to ensure unavailableList is updated
    await nextTick()
    autoSelectExistingSlots()
  } catch (error) {
    console.error('Failed to save unavailable time:', error)
    ElMessage.error(error.message || t('unavailable.saveFailed'))
  } finally {
    loading.value = false
  }
}

const loadUnavailableList = async () => {
  if (!form.teacherId || !form.date) return
  
  listLoading.value = true
  try {
    const response = await getUnavailableTime({
      id: form.teacherId,
      date: form.date
    })
    
    console.log('Loaded unavailable times:', response.data)
    
    if (response.data) {
      let dataList = Array.isArray(response.data) ? response.data : [response.data]
      // Filter out items with empty hours
      unavailableList.value = dataList.filter(item => {
        const hours = item.hours || item.uHours || ''
        return hours && hours !== '[]'
      })
      
      console.log('Filtered list:', unavailableList.value)
    } else {
      unavailableList.value = []
    }
  } catch (error) {
    console.error('Failed to load unavailable times:', error)
    unavailableList.value = []
  } finally {
    listLoading.value = false
  }
}

const handleDelete = async (item) => {
  try {
    const payload = {
      uTeacherId: form.teacherId,
      uDate: form.date,
      uHours: '[]',
      isCancel: true
    }
    
    await setUnavailableTime(payload)
    
    ElMessage.success(t('unavailable.deleteSuccess'))
    await loadUnavailableList()
  } catch (error) {
    console.error('Failed to delete unavailable time:', error)
    ElMessage.error(error.message || t('unavailable.deleteFailed'))
  }
}

onMounted(() => {
  loadTeachers()
  loadWeeklyCalendarTemplates() // Load weekly calendar templates
})

// Load weekly calendar templates
const loadWeeklyCalendarTemplates = async () => {
  try {
    const res = await getWeeklyCalendarTemplates()
    if (res.success && res.data) {
      weeklyCalendarTemplates.value = res.data
      console.log('[Unavailable] Weekly calendar templates loaded:', weeklyCalendarTemplates.value)
      // Generate initial time slots
      generateTimeSlots()
      // Generate week dates
      generateWeekDates()
    }
  } catch (error) {
    console.error('[Unavailable] Failed to load weekly calendar templates:', error)
  }
}

// Generate week dates for the current week
const generateWeekDates = () => {
  const dates = []
  const today = dayjs()
  
  for (let i = 0; i < 7; i++) {
    const date = currentWeekStart.value.add(i, 'day')
    const isPast = date.isBefore(today, 'day')
    
    if (!isPast) {
      dates.push({
        value: date.format('YYYY-MM-DD'),
        label: date.format('MM.DD'),
        weekday: getWeekdayLabel(date.day()),
        isToday: date.isSame(today, 'day'),
        isPast: isPast
      })
    }
  }
  
  weekDates.value = dates
}

// Navigate to previous/next 7-day range
const navigateWeek = (direction) => {
  // Prevent navigating to past ranges
  if (direction === -1) {
    const previousRangeStart = currentWeekStart.value.subtract(7, 'day')
    const today = dayjs().startOf('day')
    if (previousRangeStart.isBefore(today, 'day')) {
      return // Don't allow navigation to past ranges
    }
  }
  
  currentWeekStart.value = currentWeekStart.value.add(direction * 7, 'day')
  generateWeekDates()
  
  // If current selected date is not in the new range, select the first date
  const isInNewRange = weekDates.value.some(d => d.value === form.date)
  if (!isInNewRange) {
    selectDate(weekDates.value[0].value)
  }
}

// Select a date from the week calendar
const selectDate = (dateValue) => {
  form.date = dateValue
  // Time slots will be regenerated automatically by the watch
}

// Get weekday label
const getWeekdayLabel = (day) => {
  const weekdays = ['日', '一', '二', '三', '四', '五', '六']
  return `(周${weekdays[day]})`
}
</script>

<style lang="scss" scoped>
// Deep styles for Element Plus components
:deep(.el-card) {
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    border-radius: 6px;
    
    :deep(.el-card__header) {
      padding: 12px 16px;
    }
    
    :deep(.el-card__body) {
      padding: 16px;
    }
  }
  
  @media (max-width: 480px) {
    border-radius: 4px;
    
    :deep(.el-card__header) {
      padding: 10px 12px;
    }
    
    :deep(.el-card__body) {
      padding: 12px;
    }
  }
}

.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  min-height: 100vh;
  transition: background-color 0.3s ease;
  
  // Tablet responsive
  @media (max-width: 992px) {
    padding: 16px;
  }
  
  // Mobile responsive
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  // Small mobile devices
  @media (max-width: 480px) {
    padding: 8px;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  // Mobile responsive card header
  @media (max-width: 768px) {
    padding: 4px 0;
  }
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
  
  // Mobile responsive header text
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
}

.unavailable-form {
  .form-row {
    display: flex;
    margin-bottom: 20px;
    align-items: flex-start;
    
    &.full-row {
      margin-left: 0;
    }
    
    .form-label {
      width: 100px;
      flex-shrink: 0;
      padding-top: 8px;
      font-size: 14px;
      color: #606266;
    }
    
    .form-content {
      flex: 1;
      min-width: 0;
      
      // Responsive form elements
      :deep(.el-select),
      :deep(.el-date-picker) {
        width: 100%;
        
        @media (max-width: 768px) {
          :deep(.el-input__wrapper) {
            padding: 10px 12px;
            border-radius: 6px;
          }
          
          :deep(.el-input__inner) {
            font-size: 14px;
          }
        }
      }
    }
    
    // Mobile responsive form layout
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
      
      .form-label {
        width: 100%;
        padding-top: 0;
        font-size: 13px;
      }
      
      .form-content {
        width: 100%;
      }
    }
  }
  
  // Weekly calendar navigation
  .week-navigation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-bottom: 16px;
    
    .week-label {
      font-size: 15px;
      font-weight: 500;
      color: #303133;
      min-width: 120px;
      text-align: center;
    }
    
    @media (max-width: 768px) {
      gap: 12px;
      
      .week-label {
        font-size: 14px;
        min-width: 100px;
      }
    }
  }
  
  // Available dates grid (weekly calendar)
  .available-dates {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
    width: 100%;
    
    .date-item {
      padding: 12px 8px;
      background: #f5f7fa;
      border: 2px solid transparent;
      border-radius: 8px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: #ecf5ff;
        border-color: #409eff;
      }
      
      &.is-selected {
        background: #409eff;
        border-color: #409eff;
        color: #fff;
        
        .date-value,
        .date-weekday {
          color: #fff;
        }
      }
      
      &.is-past,
      &.is-disabled {
        background: #f5f7fa;
        color: #c0c4cc;
        cursor: not-allowed;
        pointer-events: none;
        
        .date-value,
        .date-weekday {
          color: #c0c4cc;
        }
      }
      
      .date-value {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 4px;
      }
      
      .date-weekday {
        font-size: 12px;
        color: #909399;
      }
    }
    
    // Tablet responsive
    @media (max-width: 992px) {
      grid-template-columns: repeat(4, 1fr);
      gap: 6px;
      
      .date-item {
        padding: 10px 6px;
        
        .date-value {
          font-size: 13px;
        }
        
        .date-weekday {
          font-size: 11px;
        }
      }
    }
    
    // Mobile responsive
    @media (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 6px;
      
      .date-item {
        padding: 8px 4px;
        border-radius: 6px;
        
        .date-value {
          font-size: 12px;
          margin-bottom: 2px;
        }
        
        .date-weekday {
          font-size: 10px;
        }
      }
    }
    
    // Small mobile
    @media (max-width: 480px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 4px;
      
      .date-item {
        padding: 6px 2px;
        
        .date-value {
          font-size: 11px;
        }
        
        .date-weekday {
          font-size: 9px;
        }
      }
    }
  }
  
  .form-label-with-count {
    width: 100px;
    flex-shrink: 0;
    padding-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    
    .form-label {
      width: auto;
      padding: 0;
      font-size: 14px;
      color: #606266;
      font-weight: 500;
    }
    
    .selected-count {
      font-size: 12px;
      color: #909399;
    }
    
    // Mobile responsive label with count
    @media (max-width: 768px) {
      width: 100%;
      padding-top: 0;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      
      .form-label {
        font-size: 13px;
      }
      
      .selected-count {
        font-size: 11px;
      }
    }
  }
  
  .slots-actions-top {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 12px;
    
    // Mobile responsive actions
    @media (max-width: 768px) {
      justify-content: center;
      gap: 12px;
      
      .el-button {
        min-width: 100px;
        padding: 10px 16px;
        font-size: 14px;
      }
    }
  }
  
  .time-slots-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 100%;
    
    .time-slot {
      padding: 12px 16px;
      background: #f5f7fa;
      border-radius: 4px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 13px;
      color: #606266;
      border: 1px solid transparent;
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      
      &:hover {
        background: #ecf5ff;
        border-color: #409EFF;
      }
      
      &.selected {
        background: #409EFF;
        border-color: #409EFF;
        color: #fff;
        font-weight: 500;
      }
      
      // Touch-friendly active state
      &:active {
        transform: scale(0.98);
      }
    }
    
    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      
      .time-slot {
        padding: 14px 8px;
        min-height: 48px;
        font-size: 14px;
        font-weight: 500;
        border-radius: 6px;
        
        // Better touch targets for mobile
        @media (hover: none) and (pointer: coarse) {
          min-height: 52px;
          padding: 16px 8px;
        }
      }
    }
    
    @media (max-width: 480px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
      
      .time-slot {
        padding: 16px 12px;
        min-height: 52px;
        font-size: 15px;
        border-radius: 8px;
      }
    }
  }
  
  .save-btn {
    height: 40px;
    font-size: 15px;
    font-weight: 500;
    
    // Mobile responsive save button
    @media (max-width: 768px) {
      height: 48px;
      font-size: 16px;
      border-radius: 8px;
    }
  }
}

.list-header {
  margin-bottom: 16px;
  
  .list-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text-primary, #333);
    transition: color 0.3s ease;
  }
  
  // Mobile responsive list header
  @media (max-width: 768px) {
    margin-bottom: 12px;
    
    .list-title {
      font-size: 14px;
    }
  }
}

.list-content {
  min-height: 100px;
  
  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 20px;
    
    // Mobile responsive empty state
    @media (max-width: 768px) {
      padding: 30px 16px;
    }
  }
  
  .unavailable-items {
    .unavailable-item {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      margin-bottom: 12px;
      background: #fff;
      transition: all 0.2s;
      
      &:hover {
        border-color: #409EFF;
        box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
      }
      
      .item-info {
        flex: 1;
        
        .item-teacher {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
        }
        
        .item-date {
          font-size: 13px;
          color: #909399;
          margin-bottom: 8px;
        }
        
        .item-hours {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 8px;
        }
      }
      
      // Mobile responsive unavailable item
      @media (max-width: 768px) {
        flex-direction: column;
        gap: 12px;
        padding: 14px;
        
        .item-info {
          width: 100%;
          
          .item-teacher {
            font-size: 13px;
          }
          
          .item-date {
            font-size: 12px;
          }
          
          .item-hours {
            gap: 6px;
            margin-top: 10px;
            
            .el-tag {
              font-size: 11px;
              padding: 4px 8px;
            }
          }
        }
        
        .el-button {
          width: 100%;
          height: 40px;
          font-size: 14px;
        }
      }
      
      // Small mobile devices
      @media (max-width: 480px) {
        padding: 12px;
        border-radius: 8px;
        
        .item-info {
          .item-teacher {
            font-size: 12px;
          }
          
          .item-date {
            font-size: 11px;
          }
        }
      }
    }
  }
}
</style>
